import { CanAccess, useNotification } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Row,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useState } from "react";

import { api } from "../api";
import {
  COPY_TOOLTIPS,
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  PAYOUT_STATUS,
  formatKurus,
} from "../labels";
import type { TutorFinance } from "../types";
import { PayoutPayDialog, type PayablePayout } from "./payout-pay-dialog";

type Revealed = { iban: string };

/**
 * "Finans" tab of a tutor (permission `payouts.view`): balance, withdrawals and payout details.
 * The full IBAN and tax number open on request (`payouts.markPaid`), recorded in the audit log.
 */
export function TutorFinanceTab({ tutorId }: { tutorId: string }) {
  const { open } = useNotification();
  const [revealed, setRevealed] = useState<Revealed | null>(null);
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState<PayablePayout | null>(null);
  const {
    data: finance,
    isPending,
    error,
    refetch,
  } = useQuery({
    queryKey: ["office", "tutor-finance", tutorId],
    queryFn: () => api<TutorFinance>(`/api/office/tutors/${tutorId}/finance`),
  });

  async function reveal() {
    setBusy(true);
    try {
      setRevealed(
        await api<Revealed>(`/api/office/tutors/${tutorId}/payout-info`, { method: "POST" }),
      );
    } catch (failure) {
      open?.({
        type: "error",
        message: failure instanceof Error ? failure.message : "Hata oluştu.",
      });
    } finally {
      setBusy(false);
    }
  }

  if (isPending) return <Spin />;
  if (error || !finance) {
    return (
      <Alert type="error" showIcon message={error?.message ?? "Finans bilgisi yüklenemedi."} />
    );
  }

  const info = finance.payoutInfo;
  return (
    <Row gutter={[24, 24]}>
      <Col xs={24}>
        <Row gutter={[16, 16]}>
          {[
            { title: "Çekilebilir", value: finance.withdrawableKurus },
            { title: "14 günlük beklemede", value: finance.onHoldKurus },
            { title: "Ödeme bekleyen", value: finance.pendingKurus },
            { title: "Ödenen", value: finance.paidKurus },
            { title: "Toplam kazanç", value: finance.totalEarnedKurus },
          ].map((item) => (
            <Col key={item.title} xs={12} md={8} xl={4}>
              <Card size="small">
                <Statistic
                  title={item.title}
                  value={formatKurus(item.value)}
                  valueStyle={{ fontSize: 18 }}
                />
              </Card>
            </Col>
          ))}
          <Col xs={12} md={8} xl={4}>
            <Card size="small">
              <Statistic
                title="Tamamlanan ders"
                value={finance.completedLessons}
                valueStyle={{ fontSize: 18 }}
              />
            </Card>
          </Col>
        </Row>
      </Col>

      <Col xs={24} lg={10}>
        <Card size="small" title="Ödeme bilgileri">
          {info ? (
            <>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Hesap sahibi">
                  {info.accountHolder ?? "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Banka">{info.bankName ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="SWIFT">
                  {info.swiftCode ? (
                    <Typography.Text
                      code
                      copyable={{ text: info.swiftCode, tooltips: COPY_TOOLTIPS }}
                    >
                      {info.swiftCode}
                    </Typography.Text>
                  ) : (
                    "—"
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="IBAN">
                  {revealed ? (
                    <Typography.Text
                      code
                      copyable={{ text: revealed.iban.replace(/\s/g, ""), tooltips: COPY_TOOLTIPS }}
                      data-testid="tutor-iban"
                    >
                      {revealed.iban}
                    </Typography.Text>
                  ) : (
                    `TR•• •••• ${info.ibanLast4}`
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Son güncelleme">
                  {dayjs(info.updatedAt).format(DATE_TIME_FORMAT)}
                </Descriptions.Item>
              </Descriptions>
              {revealed ? (
                <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
                  Görüntülemen İşlem kayıtlarına yazıldı.
                </Typography.Paragraph>
              ) : (
                <CanAccess resource="payouts" action="pay">
                  <Button onClick={reveal} loading={busy} style={{ marginTop: 12 }}>
                    IBAN'ı göster
                  </Button>
                </CanAccess>
              )}
            </>
          ) : (
            <Empty description="Eğitmen banka bilgilerini henüz girmedi" />
          )}
        </Card>
      </Col>

      <Col xs={24} lg={14}>
        <Card size="small" title="Ödeme talepleri">
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={finance.payouts}
            locale={{ emptyText: "Henüz ödeme talebi yok" }}
            scroll={{ x: "max-content" }}
            columns={[
              {
                title: "Tutar",
                dataIndex: "amountKurus",
                render: (value: number) => formatKurus(value),
              },
              {
                title: "Talep",
                dataIndex: "requestedAt",
                render: (value: string) => dayjs(value).format(DATE_TIME_FORMAT),
              },
              {
                title: "Ödeme günü",
                dataIndex: "scheduledFor",
                render: (value: string) => dayjs(value).format(DATE_FORMAT),
              },
              {
                title: "Durum",
                dataIndex: "status",
                render: (value: string, payout: TutorFinance["payouts"][number]) => (
                  <>
                    <Tag color={PAYOUT_STATUS[value]?.color}>{PAYOUT_STATUS[value]?.label}</Tag>
                    {payout.paidAt ? dayjs(payout.paidAt).format(DATE_FORMAT) : null}
                  </>
                ),
              },
              { title: "IBAN", dataIndex: "ibanLast4", render: (value: string) => `•••• ${value}` },
              {
                title: "",
                key: "pay",
                // Paid from here as from /payouts (same dialog, same audit).
                render: (_: unknown, payout: TutorFinance["payouts"][number]) =>
                  payout.status === "REQUESTED" ? (
                    <CanAccess resource="payouts" action="pay">
                      <Button size="small" type="primary" onClick={() => setPaying(payout)}>
                        Öde
                      </Button>
                    </CanAccess>
                  ) : null,
              },
            ]}
          />
        </Card>
      </Col>
      <PayoutPayDialog payout={paying} onClose={() => setPaying(null)} onPaid={() => refetch()} />
    </Row>
  );
}
